import type { SpriteCoords, SubtileGrid, LevelTile } from "@/types/tileset.types"
import { GRID_WIDTH, GRID_HEIGHT } from "@/data/game-settings"

// ============================================
// SHARED PATH AUTOTILER (16px subtile level)
// ============================================
// Single source of truth for how a dirt path is autotiled against grass.
// Every sprite coordinate below was verified against ground_grasss.png by
// rendering test bends at both path widths - the previous mappings pointed
// two corners at EMPTY sheet cells (4,2)/(5,2), which drew nothing and let
// the page background show through at every path bend, and had the two
// south-facing corners swapped left/right.
//
// Sheet convention for convex (outer) corners: the tile is mostly grass with
// a dirt wedge entering from the corner the path continues toward.
// Inner corners are mostly dirt with a grass tuft poking in at one diagonal.

const GRASS: SpriteCoords = { x: 2, y: 1 } // solid grass
const DIRT: SpriteCoords = { x: 9, y: 8 } // solid dirt (blob centre)

// Edges: dirt with a grass fringe on one side
const EDGE_N: SpriteCoords = { x: 2, y: 2 } // grass to the north
const EDGE_S: SpriteCoords = { x: 2, y: 3 } // grass to the south
const EDGE_W: SpriteCoords = { x: 3, y: 1 } // grass to the west
const EDGE_E: SpriteCoords = { x: 1, y: 1 } // grass to the east

// Convex corners, named for where the path continues (dirt wedge direction)
const CORNER_SE: SpriteCoords = { x: 4, y: 0 } // path to S+E - dirt wedge SE
const CORNER_SW: SpriteCoords = { x: 5, y: 0 } // path to S+W - dirt wedge SW
const CORNER_NE: SpriteCoords = { x: 4, y: 1 } // path to N+E - dirt wedge NE
const CORNER_NW: SpriteCoords = { x: 5, y: 1 } // path to N+W - dirt wedge NW

// Inner corners, named for the diagonal where grass pokes into the path
const INNER_NW: SpriteCoords = { x: 1, y: 2 } // grass tuft at NW
const INNER_NE: SpriteCoords = { x: 3, y: 2 } // grass tuft at NE
const INNER_SW: SpriteCoords = { x: 4, y: 4 } // grass tuft at SW
const INNER_SE: SpriteCoords = { x: 5, y: 4 } // grass tuft at SE

export const GRASS_SUBTILE: SpriteCoords = GRASS

/** Set membership test for path subtiles */
export type SubtilePathTest = (sx: number, sy: number) => boolean

/**
 * Pick the sprite for one 16px subtile of path based on its 8 neighbours.
 */
export function computePathSubtileSprite(isPath: SubtilePathTest, sx: number, sy: number): SpriteCoords {
  if (!isPath(sx, sy)) return GRASS

  const n = isPath(sx, sy - 1)
  const e = isPath(sx + 1, sy)
  const s = isPath(sx, sy + 1)
  const w = isPath(sx - 1, sy)
  const cardinals = (n ? 1 : 0) + (e ? 1 : 0) + (s ? 1 : 0) + (w ? 1 : 0)

  if (cardinals === 4) {
    // Surrounded - solid dirt unless a diagonal is grass (inner corner)
    if (!isPath(sx - 1, sy - 1)) return INNER_NW
    if (!isPath(sx + 1, sy - 1)) return INNER_NE
    if (!isPath(sx - 1, sy + 1)) return INNER_SW
    if (!isPath(sx + 1, sy + 1)) return INNER_SE
    return DIRT
  }

  if (cardinals === 3) {
    // One grass side - edge tile
    if (!n) return EDGE_N
    if (!e) return EDGE_E
    if (!s) return EDGE_S
    return EDGE_W
  }

  if (cardinals === 2) {
    // Convex corner (or, degenerate, a 1-subtile corridor)
    if (s && e) return CORNER_SE
    if (s && w) return CORNER_SW
    if (n && e) return CORNER_NE
    if (n && w) return CORNER_NW
    return DIRT // opposite cardinals - corridor
  }

  // Path stub / endpoint
  return DIRT
}

/** Compute the 2x2 subtile grid for the logical tile whose NW subtile is (baseSx, baseSy) */
export function computeTileSubtiles(isPath: SubtilePathTest, baseSx: number, baseSy: number): SubtileGrid {
  return [
    [
      computePathSubtileSprite(isPath, baseSx, baseSy),
      computePathSubtileSprite(isPath, baseSx + 1, baseSy),
    ],
    [
      computePathSubtileSprite(isPath, baseSx, baseSy + 1),
      computePathSubtileSprite(isPath, baseSx + 1, baseSy + 1),
    ],
  ]
}

/**
 * Rasterize an axis-aligned waypoint path (world pixels) into the set of
 * path subtiles. Works for any even width: level 1's 64px path centred on
 * tile boundaries and the 32px paths centred on tile centres both come out
 * symmetric. Off-grid waypoints (spawn entries above the map) are kept in
 * the set so entrances/exits read as continuing off-screen instead of being
 * capped with a fringe.
 */
export function rasterizePath(
  waypoints: Array<{ x: number; y: number }>,
  pathWidthPx: number,
): Set<number> {
  const SUB = 16
  const set = new Set<number>()
  const half = pathWidthPx / 2

  const mark = (sx: number, sy: number) => set.add(subtileKey(sx, sy))

  for (let i = 0; i < waypoints.length - 1; i++) {
    const a = waypoints[i]
    const b = waypoints[i + 1]
    if (Math.round(a.x) === Math.round(b.x)) {
      // Vertical segment - band spans [x-half, x+half) in px
      const s0 = Math.round((a.x - half) / SUB)
      const s1 = Math.round((a.x + half) / SUB) - 1
      const y0 = Math.min(a.y, b.y) - half
      const y1 = Math.max(a.y, b.y) + half
      for (let sy = Math.round(y0 / SUB); sy < Math.round(y1 / SUB); sy++) {
        for (let sx = s0; sx <= s1; sx++) mark(sx, sy)
      }
    } else {
      // Horizontal segment
      const s0 = Math.round((a.y - half) / SUB)
      const s1 = Math.round((a.y + half) / SUB) - 1
      const x0 = Math.min(a.x, b.x) - half
      const x1 = Math.max(a.x, b.x) + half
      for (let sx = Math.round(x0 / SUB); sx < Math.round(x1 / SUB); sx++) {
        for (let sy = s0; sy <= s1; sy++) mark(sx, sy)
      }
    }
  }
  return set
}

// Numeric subtile key; offset keeps off-grid (negative) coords unambiguous
const KEY_OFFSET = 64
const KEY_STRIDE = 4096
export function subtileKey(sx: number, sy: number): number {
  return (sy + KEY_OFFSET) * KEY_STRIDE + (sx + KEY_OFFSET)
}

/**
 * Build the full ground tile grid for a path.
 */
export function buildGroundTiles(pathSubtiles: Set<number>): LevelTile[][] {
  const isPath: SubtilePathTest = (sx, sy) => pathSubtiles.has(subtileKey(sx, sy))
  const tiles: LevelTile[][] = []
  for (let ty = 0; ty < GRID_HEIGHT; ty++) {
    const row: LevelTile[] = []
    for (let tx = 0; tx < GRID_WIDTH; tx++) {
      const sx = tx * 2
      const sy = ty * 2
      const hasPath =
        isPath(sx, sy) || isPath(sx + 1, sy) || isPath(sx, sy + 1) || isPath(sx + 1, sy + 1)
      row.push(
        hasPath
          ? { ground: "path", subtiles: computeTileSubtiles(isPath, sx, sy) }
          : {
              ground: "grass",
              subtiles: [
                [GRASS, GRASS],
                [GRASS, GRASS],
              ],
            },
      )
    }
    tiles.push(row)
  }
  return tiles
}

/** Logical tiles touched by the path (for decoration keep-out zones) */
export function pathTileSet(pathSubtiles: Set<number>): Set<number> {
  const tiles = new Set<number>()
  const isPath = (sx: number, sy: number) => pathSubtiles.has(subtileKey(sx, sy))
  for (let ty = -2; ty < GRID_HEIGHT + 2; ty++) {
    for (let tx = -2; tx < GRID_WIDTH + 2; tx++) {
      const sx = tx * 2
      const sy = ty * 2
      if (isPath(sx, sy) || isPath(sx + 1, sy) || isPath(sx, sy + 1) || isPath(sx + 1, sy + 1)) {
        tiles.add(subtileKey(tx, ty))
      }
    }
  }
  return tiles
}
